import { zodResolver } from '@hookform/resolvers/zod';
import { ApiClientError } from '@recipes/api-client';
import { changePasswordRequestSchema, type ChangePasswordRequest } from '@recipes/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Skeleton } from '@recipes/ui';
import { format } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { LogOut, Monitor } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FormField } from '@/components/forms/form-field';
import { IdentityAccountLink, useAuthMode } from '@/features/auth/identity';
import { toast } from '@/components/feedback/toaster';
import { PageHeader } from '@/components/layout/page-header';
import { PasswordField } from '@/features/profile/password-field';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

function ChangePasswordForm() {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<ChangePasswordRequest>({
    resolver: zodResolver(changePasswordRequestSchema),
    defaultValues: { currentPassword: '', newPassword: '' },
  });
  const newPassword = form.watch('newPassword');
  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.auth.changePassword(values);
      toast.success(t('profile.security.passwordChanged'));
      form.reset();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'INVALID_CREDENTIALS')
        setError(t('profile.security.wrongPassword'));
      else setError(describeError(err));
    }
  });
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="change-password-form">
      {error && <Alert variant="destructive">{error}</Alert>}
      <FormField
        label={t('profile.security.currentPassword')}
        error={form.formState.errors.currentPassword && t('auth.errors.passwordRequired')}
        required
      >
        <PasswordField
          autoComplete="current-password"
          data-testid="current-password"
          {...form.register('currentPassword')}
        />
      </FormField>
      <FormField
        label={t('auth.newPassword')}
        error={form.formState.errors.newPassword && t('auth.errors.passwordWeak')}
        required
      >
        <PasswordField
          autoComplete="new-password"
          value={newPassword}
          showStrength
          data-testid="new-password"
          {...form.register('newPassword')}
        />
      </FormField>
      <Button
        type="submit"
        loading={form.formState.isSubmitting}
        data-testid="change-password-submit"
      >
        {t('profile.security.changePassword')}
      </Button>
    </form>
  );
}

export function SecurityPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const authMode = useAuthMode();
  const dateLocale = currentLocale() === 'de' ? deLocale : enUS;
  const sessions = useQuery({ queryKey: queryKeys.sessions, queryFn: () => api.auth.sessions() });
  const revoke = useMutation({
    mutationFn: (id: string) => api.auth.revokeSession(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
    onError: (e) => toastError(e),
  });
  const revokeOthers = useMutation({
    mutationFn: () => api.auth.revokeOtherSessions(),
    onSuccess: () => {
      toast.success(t('profile.security.othersRevoked'));
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    },
    onError: (e) => toastError(e),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-8 p-4 md:p-6" data-testid="security-page">
      <PageHeader
        eyebrow={
          <Link to="/profile" className="underline-offset-4 hover:underline">
            ← {t('profile.title')}
          </Link>
        }
        title={t('profile.security.title')}
      />
      <section aria-labelledby="password-heading" className="space-y-3">
        <h2 id="password-heading" className="text-lg font-semibold">
          {t('profile.security.changePassword')}
        </h2>
        {authMode.mode === 'oidc' && authMode.identity ? (
          <div className="space-y-3 rounded-lg border bg-card p-4" data-testid="security-identity">
            <p className="text-sm text-muted-foreground">{t('auth.identity.managedAt')}</p>
            <IdentityAccountLink identity={authMode.identity} />
          </div>
        ) : (
          <ChangePasswordForm />
        )}
      </section>
      <section aria-labelledby="sessions-heading" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="sessions-heading" className="text-lg font-semibold">
            {t('profile.security.sessions')}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => revokeOthers.mutate()}
            loading={revokeOthers.isPending}
            disabled={(sessions.data?.length ?? 0) < 2}
          >
            <LogOut aria-hidden /> {t('profile.security.revokeOthers')}
          </Button>
        </div>
        {sessions.isPending ? (
          <Skeleton className="h-24" />
        ) : sessions.isError ? (
          <Alert variant="destructive">{describeError(sessions.error)}</Alert>
        ) : (
          <ul className="divide-y rounded-lg border" data-testid="session-list">
            {sessions.data.map((s) => (
              <li key={s.id} className="flex items-center gap-3 p-3">
                <Monitor className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1 text-sm">
                  <p className="truncate font-medium">
                    {s.deviceInfo ?? t('profile.security.unknownDevice')}
                    {s.current && (
                      <Badge variant="success" className="ml-2">
                        {t('profile.security.current')}
                      </Badge>
                    )}
                  </p>
                  <p className="text-muted-foreground">
                    {t('profile.security.lastUsed')}:{' '}
                    {s.lastUsedAt
                      ? format(new Date(s.lastUsedAt), 'PPp', { locale: dateLocale })
                      : '–'}{' '}
                    · {t('profile.security.expires')}:{' '}
                    {format(new Date(s.expiresAt), 'PP', { locale: dateLocale })}
                  </p>
                </div>
                {!s.current && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revoke.mutate(s.id)}
                    loading={revoke.isPending && revoke.variables === s.id}
                    data-testid="revoke-session"
                  >
                    {t('profile.security.revoke')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
